/**
 * cellObjects.js — the object tree behind a cell, and the rules for what may be done to it.
 *
 * Pure: no imports beyond schema helpers, no state, no React.
 *
 * A cell owns three kinds of object, and they answer different questions:
 *
 *   meta       the cell's whole life, distilled — one record per run outcome, decision and artifact
 *   sessions   one per worker_task run, raw: the harness transcript and the worker's control surface
 *   artifacts  what a run published, promoted out of the workspace and addressable for ever
 *
 * The session list is built from the **run log**, not from the object namespace. The log IS the
 * workflow cursor, so it knows a run exists the moment it is opened — before the session object has
 * been created, and before the browser's copy of the object list has caught up. Listing would show
 * a run one refresh late and an `external_input` run (which opens no session at all) never.
 */

import { artifactsPrefix, sessionKey, sessionThreadAlias } from './schema';

export const NODE_META = 'meta';

export function sessionNodeId(key) {
  return `session:${key}`;
}

export function artifactNodeId(alias) {
  return `artifact:${alias}`;
}

/**
 * Build the tree for one cell.
 *
 * @param {Object} options
 * @param {string} options.missionId
 * @param {string} options.cellId
 * @param {Array}  options.log      - cell.props.log, the run log (ordered, append-only)
 * @param {Array<string>} options.aliases - every session-object alias currently known
 * @returns {{ meta: Object, sessions: Array }}
 */
export function buildCellTree({ missionId, cellId, log, aliases }) {
  const known = Array.isArray(aliases) ? aliases : [];
  const entries = Array.isArray(log) ? log : [];

  const sessions = entries
    // An external_input run opens no session — the human answers on the meta-thread — so it has
    // nothing to drill into and does not appear as a node.
    .filter(entry => entry && entry.session)
    .map(entry => {
      const key = entry.session || sessionKey(entry.step, entry.run);
      const prefix = artifactsPrefix(missionId, cellId, entry.step, entry.run);
      return {
        id: sessionNodeId(key),
        key,
        step: entry.step,
        run: entry.run,
        state: entry.state,
        label: entry.label || null,
        openedAt: entry.opened_at || null,
        closedAt: entry.closed_at || null,
        alias: sessionThreadAlias(missionId, cellId, entry.step, entry.run),
        artifacts: known
          .filter(alias => alias.startsWith(prefix))
          .sort()
          .map(alias => ({ id: artifactNodeId(alias), alias, path: alias.slice(prefix.length) }))
      };
    });

  return {
    meta: { id: NODE_META, alias: `missions/${missionId}/cells/${cellId}/meta` },
    sessions
  };
}

/** Find a node by id across the whole tree. Returns `{ kind, ... }` or null. */
export function findNode(tree, nodeId) {
  if (!tree || !nodeId) return null;
  if (nodeId === NODE_META) return { kind: 'meta', ...tree.meta };
  for (const session of tree.sessions) {
    if (session.id === nodeId) return { kind: 'session', ...session };
    for (const artifact of session.artifacts) {
      if (artifact.id === nodeId) return { kind: 'artifact', ...artifact, session };
    }
  }
  return null;
}

/**
 * Which input control a cell should offer — and it is never both.
 *
 * A **gate** is an `external_input` step: the answer is a durable, carryover-visible record on the
 * meta-thread, and its label routes the outgoing edge. An **in-flight** request is a running agent
 * blocked mid-turn: the answer is a plain text item on its session thread, matched by the worker's
 * prompt bridge, and the same run simply resumes.
 *
 * Offering both at once is not a layout mistake, it is a correctness one: the worker resolves any
 * text item against a pending prompt BEFORE treating it as input, so a note typed as "steering"
 * while a prompt is open silently becomes the answer to a question the person was not shown.
 *
 * @param {Object} cell - the cell vertex ({ props })
 * @returns {{ mode: 'gate'|'in_flight'|'none', request: Object|null }}
 */
export function inputAffordance(cell) {
  // `props.request` is published in every status that takes input, so a request alone no longer
  // means a question is open — `mode` is what says one is. A working cell's steering box and a
  // failed cell's resume box carry neither `gate` nor `in_flight`, and are not affordances to
  // ANSWER anything.
  const request = (cell && cell.props && cell.props.request) || null;
  if (!request) return { mode: 'none', request: null };
  if (request.mode === 'in_flight') return { mode: 'in_flight', request };
  if (request.mode === 'gate') return { mode: 'gate', request };
  return { mode: 'none', request: null };
}

/*
 * The two words the worker's prompt bridge reads back on an `approval`, and the reason an approval
 * has no free-text path anywhere in this widget.
 *
 * The bridge lowercases and trims the answer and looks it up in
 * `{approve, approved, yes, y, ok, allow, 1}`. Membership is the WHOLE decision: everything else is
 * a Deny, and the agent is then told the human refused. So "go ahead, but be careful" denies. So
 * does "yes, but only the first one". So does a harness's own affirmative label — Claude Code's
 * "Yes, allow" is not in the set either, which is why an approval never renders the request's
 * options as answer buttons: their labels are written for a person, and the bridge reads none of
 * them as consent.
 *
 * An approval's answer text is therefore never the human's words and never the harness's words. It
 * is one of these two constants, chosen by which button was pressed.
 */
const APPROVE_TEXT = 'approve';
const DENY_TEXT = 'deny';

/**
 * What an in-flight request may be answered WITH — expressed as the exact text the worker will read
 * back, so nothing the UI can express is something the bridge will misread.
 *
 * `label` is what the person reads and `text` is what is posted, and on an approval the two
 * deliberately differ (see above). For a `choice` they are the same string: the bridge resolves an
 * option by 1-based index or by its label, and the label is the one of those two that cannot drift
 * if the orchestrator's list and the harness's list are ordered differently.
 *
 * @param {Object} request - `cell.props.request` with `mode: 'in_flight'`
 * @returns {{ choices: Array<{ label: string, text: string, affirmative?: boolean }>, freeText: boolean }}
 */
/**
 * The questions a request asks, each with the control it needs — one entry per question.
 *
 * **A prompt is a LIST of questions, and the list is the contract.** An `ask_user` call may ask
 * four, mixing single-select, multi-select and free text, and each is answered on its own. Flattened
 * into one control the person answers one of them and the worker maps that single string onto all
 * four — which is what a feed once showed: four questions answered "Investment portfolio" four
 * times. So nothing is flattened here, and nothing is invented: the shapes come off the request as
 * the harness wrote them.
 *
 * An approval is the degenerate case and is treated as one — a single question with two fixed
 * choices and no free text, because free text on an approval is a Deny wearing a "yes" (the worker
 * reads anything outside seven affirmative words as a denial). **codex has no other kind**, so this
 * branch is the whole of its human channel.
 *
 * @param {Object} request - `cell.props.request`
 * @returns {Array<{ prompt: string, header?: string, mode: 'approval'|'one'|'many'|'text',
 *                   choices: Array<{ label: string, description: string, text?: string,
 *                                    affirmative?: boolean }>, freeText: boolean }>}
 */
export function inFlightQuestions(request) {
  const source = request || {};
  if (source.kind === 'approval') {
    return [{
      prompt: source.prompt || '',
      mode: 'approval',
      // Approve or Deny; there is no third thing an approval means, and the agent is parked until
      // one of them is said.
      required: true,
      choices: [
        { label: 'Approve', description: '', text: APPROVE_TEXT, affirmative: true },
        { label: 'Deny', description: '', text: DENY_TEXT }
      ],
      freeText: false
    }];
  }
  const questions = Array.isArray(source.questions) ? source.questions : [];
  if (questions.length > 0) {
    return questions.map(question => {
      const choices = (question.options || [])
        .filter(option => option && option.label)
        .map(option => ({ label: option.label, description: option.description || '' }));
      return {
        prompt: question.question || '',
        header: question.header || '',
        // A question with no options is free text. One WITH options still takes free text — Claude
        // Code's own tool advertises "the user can always answer with free text via 'Other'", and the
        // worker honours it: a token matching no option is handed back verbatim. A surface offering
        // only the listed options would be narrower than the contract it implements.
        mode: choices.length === 0 ? 'text' : (question.multi_select ? 'many' : 'one'),
        choices,
        // **A question whose answer ROUTES takes no free text.** `routes` marks the one whose answer
        // names the outgoing edge, and a label nobody drew an edge for goes nowhere: the orchestrator
        // refuses it (`route.route_answer`) and asks again, which is a dead end offered as an option.
        // What a person wants to add at a gate is a COMMENT, not a third exit, and that is a field of
        // its own at the foot.
        //
        // Every other question does take it. Claude Code's own tool advertises "the user can always
        // answer with free text via 'Other'", and the worker honours it — a token matching no option
        // is handed back verbatim — so a surface offering only the listed options would be narrower
        // than the contract it implements.
        routes: question.routes === true,
        freeText: question.routes !== true,
        // **A question with a list requires an answer from it.** An empty answer to "which of these"
        // is not an answer at all — the agent asked because it cannot proceed without one, and the
        // free-text row is there for the person whose answer is none of the listed ones. A question
        // with no list is the open-ended kind — steer the run, pick it back up — where saying
        // nothing is a legitimate "carry on".
        //
        // Beyond that it is the QUESTION's to say, not a status this control would have to
        // recognise: `completed` sets the flag on a listless ask, because a cell that has already
        // said it was finished has no "carry on" without saying what more is wanted.
        required: question.required === true || choices.length > 0
      };
    });
  }
  // No questions at all is not a shape anything writes: every producer normalises into `questions`,
  // and the coarse `options` / `allow_free_text` view that used to sit beside them is gone.
  return [];
}

/**
 * The answer set to post, in the harness's own shape — one entry per question.
 *
 * @param {Array} questions - from `inFlightQuestions`
 * @param {Array<Array<string>>} picked - the chosen values, per question
 * @returns {Array<{ question: string, answer: string[] }>}
 */
export function inFlightAnswerSet(questions, picked) {
  return questions.map((question, index) => ({
    question: question.prompt,
    answer: (picked[index] || []).filter(value => value !== undefined && value !== null && value !== '')
  }));
}

/**
 * The choice a recorded answer value names, or null — matched by label or by posted text,
 * case-insensitively, because the value on the wire is whichever of the two the answering surface
 * wrote (this app writes `text`; a person typing into the raw thread writes anything).
 *
 * Matching order: exact label, exact text, case-insensitive label, case-insensitive text. First hit
 * wins. `null` means genuinely free text — a value naming no choice at all — and the caller keeps
 * that as the "own words" row rather than forcing a match.
 *
 * @param {Array<{label: string, text?: string}>} choices
 * @param {*} value - a recorded answer entry
 * @returns {Object|null}
 */
export function choiceForAnswer(choices, value) {
  const list = Array.isArray(choices) ? choices : [];
  if (typeof value !== 'string' || value === '') return null;
  const byLabel = list.find(choice => choice && choice.label === value);
  if (byLabel) return byLabel;
  const byText = list.find(choice => choice && choice.text === value);
  if (byText) return byText;
  const lower = value.toLowerCase();
  const byLabelCi = list.find(choice => choice && typeof choice.label === 'string'
    && choice.label.toLowerCase() === lower);
  if (byLabelCi) return byLabelCi;
  const byTextCi = list.find(choice => choice && typeof choice.text === 'string'
    && choice.text.toLowerCase() === lower);
  return byTextCi || null;
}

export function inFlightAnswers(request) {
  const source = request || {};
  if (source.kind === 'approval') {
    return {
      choices: [
        { label: 'Approve', text: APPROVE_TEXT, affirmative: true },
        { label: 'Deny', text: DENY_TEXT }
      ],
      freeText: false
    };
  }
  const choices = (source.options || [])
    .filter(option => option && option.label)
    .map(option => ({ label: option.label, text: option.label }));
  // `allow_free_text` decides, with one exception: a `question` carrying no options IS free text —
  // the bridge hands its raw text back as the answer — and honouring a missing flag there would
  // leave a parked agent with no reachable control at all.
  const freeText = source.allow_free_text === true
    || (source.kind === 'question' && choices.length === 0);
  return { choices, freeText };
}

/**
 * What each answered question was answered WITH — `input_needed` item id → the answer set.
 *
 * A question that has been answered should read exactly as it did when it was open: the same
 * questions, the same options, with the chosen ones marked. The answers are not on the question's
 * own record — nothing can be, since it was written before anybody answered — they are on the
 * `input_provided` that followed it, which is where a person's decision belongs.
 *
 * Paired IN ORDER within a run, because a run may ask several times: the first question of a run
 * takes the first answer of that run, and so on. Anything left over is a question still open, or one
 * superseded before it was answered, and neither gets an answer set.
 *
 * @param {Array} items - meta-thread items, chronological
 * @returns {Object} item id → `[{ question, answer: [...] }]`
 */
export function answersByQuestion(items) {
  const waiting = new Map();   // "step/run" → the ids of its unanswered questions, oldest first
  const found = {};
  for (const item of items || []) {
    const metadata = (item && item.metadata) || {};
    const key = `${metadata.step}/${metadata.run}`;
    if (metadata.type === 'input_needed') {
      if (!waiting.has(key)) waiting.set(key, []);
      waiting.get(key).push(item.id);
      continue;
    }
    // A person's answer is an `intent · answer` — the one record type every question is answered
    // with. `input_provided` is what the ORCHESTRATOR writes for what the run received, and reading
    // it here would show its account of an answer beside the person's own.
    if (metadata.type !== 'intent' || metadata.event !== 'answer') continue;
    // **An answer with nothing in it is not an answer**, and the orchestrator reads it the same way
    // (`observe._says_something`): the worker discards an inbound item with no text, so an empty
    // record can never reach the agent and the run stays parked. Counting it here would take the
    // control away from the one person who could unpark it — which is exactly what stranded a live
    // cell in `input_needed` with an empty bubble under the question and no way out of it.
    if (!(Array.isArray(metadata.answers) && metadata.answers.length > 0)
        && !bodyTextOf(item).trim()) continue;
    const queue = waiting.get(key);
    if (!queue || queue.length === 0) continue;
    const id = queue.shift();
    // The structured set when the answering surface produced one; otherwise the prose it wrote,
    // which is all an out-of-band answer has. Both say what was answered — one per question, or one
    // line — and the renderer shows whichever it is given.
    const answers = metadata.answers;
    found[id] = Array.isArray(answers) && answers.length > 0
      ? answers
      : [{ question: '', answer: [bodyTextOf(item)] }];
  }
  return found;
}

function bodyTextOf(item) {
  return ((item && item.content) || [])
    .filter(part => part && part.type === 'text' && part.text)
    .map(part => part.text)
    .join('\n');
}

/**
 * The identity of one in-flight request: what makes THIS question this question.
 *
 * `step` and `run` are not enough on their own — an agent can ask, be answered, and ask again
 * without the cell's cursor moving — so the prompt's own id leads and the item that raised it backs
 * it up. Used to notice that the request behind a control has been REPLACED rather than answered,
 * which is the one case where a control that has already been used must come back.
 * @param {Object} request
 * @returns {string}
 */
export function inFlightKey(request) {
  const source = request || {};
  const ref = source.thread_ref || {};
  const prompt = source.worker_prompt || {};
  return [prompt.prompt_id, ref.alias, ref.item_id, source.step, source.run]
    .map(part => (part === undefined || part === null ? '' : String(part)))
    .join('|');
}

/**
 * Which `input_needed` record in a meta-thread carries the cell's OPEN request — the id of the last
 * one, or null when nothing is open.
 *
 * An `input_needed` record says a question was asked at a `step` and `run`; the cell says which
 * question is still waiting. Matching on `step`/`run` alone therefore lights up EVERY question of a
 * run that asked more than once, and each of them offers to answer the request that is open now.
 * Two of those answers is not a duplicate: the first resolves the prompt and the second, arriving
 * after the bridge has let go, becomes the prompt for the agent's NEXT turn. The last matching
 * record is the one the open request belongs to; the earlier ones are history.
 *
 * The record carries its own `request` snapshot, but that is the question AS ASKED, frozen — it
 * cannot say whether anybody is still waiting on it. Only the cell can, which is why liveness is
 * decided here and never read off the record.
 *
 * @param {Array} items - meta-thread items, chronological
 * @param {Object} request - the cell's open request, or null
 * @returns {string|null}
 */
export function openQuestionId(items, request) {
  if (!request) return null;
  const openPrompt = promptIdOf(request);
  let openId = null;
  for (const item of items || []) {
    const metadata = (item && item.metadata) || {};
    if (metadata.type !== 'input_needed') continue;
    if (metadata.step !== request.step || metadata.run !== request.run) continue;
    // A record that names a DIFFERENT prompt is provably not the open one, and skipping it is the
    // whole of the identity check: step and run match every question a run asked, so without this a
    // run's earlier question is as eligible as its current one — and being the last thing left is
    // how it used to end up wearing the controls, and the live question's prose with them.
    //
    // Rejection needs both ids, never an absence. A record carrying no snapshot cannot prove it is
    // not the open question, and a gate carries no prompt id at all; both stay eligible, and for
    // them being the last raise of the run is all the evidence there is.
    const recordPrompt = promptIdOf(metadata.request);
    if (openPrompt && recordPrompt && recordPrompt !== openPrompt) continue;
    openId = item.id || null;
  }
  return openId;
}

/**
 * The worker's id for the prompt a request was raised from, or "" for a gate — which the
 * orchestrator raises itself and asks once per run, so the run identifies it on its own.
 */
function promptIdOf(request) {
  return (((request || {}).worker_prompt) || {}).prompt_id || '';
}

/** Read `instance.state` off a session thread's value. */
export function sessionState(value) {
  const metadata = (value && value.thread && value.thread.metadata) || {};
  return (metadata.instance && metadata.instance.state) || null;
}

/** Read the artifact block a published object carries. */
export function artifactMeta(value) {
  const message = (value && value.message) || {};
  return ((message.metadata || {}).artifact) || {};
}

/** The text body of a message object — an artifact's contents. */
export function artifactText(value) {
  const content = ((value && value.message) || {}).content || [];
  return content.filter(part => part && part.type === 'text').map(part => part.text || '').join('\n');
}

/**
 * Sort a cell's session-object aliases into what a person would call them.
 *
 * Used to say what deleting a cell will destroy, in the words the cell surface already uses for the
 * same things — "meta thread", "run transcript", "artifact" — rather than as a list of paths.
 *
 * Anything that matches none of the three shapes still counts, under `other`. The namespace under a
 * cell belongs to the system, not to this file: a future kind of object must be DELETED with the
 * cell whether or not this function has learned to name it, and the count is what the caller acts
 * on. Silently dropping the unrecognised would leave exactly the orphan this whole path exists to
 * prevent.
 *
 * @param {string[]} aliases - every alias under the cell's prefix
 * @returns {{ meta: string[], transcripts: string[], artifacts: string[], other: string[], total: number }}
 */
export function classifyCellObjects(aliases) {
  const all = Array.isArray(aliases) ? aliases : [];
  const groups = { meta: [], transcripts: [], artifacts: [], other: [] };
  all.forEach(alias => {
    if (/\/meta$/.test(alias)) groups.meta.push(alias);
    else if (/\/sessions\/[^/]+\/thread$/.test(alias)) groups.transcripts.push(alias);
    else if (/\/sessions\/[^/]+\/artifacts\//.test(alias)) groups.artifacts.push(alias);
    else groups.other.push(alias);
  });
  return { ...groups, total: all.length };
}

/**
 * "its meta thread, 2 run transcripts and 5 artifacts" — the phrase for a confirmation.
 *
 * Empty groups are omitted rather than printed as zeroes, so the sentence names only what actually
 * goes. An unrecognised object is counted as an object, which is honest about there being something
 * without inventing a name for it.
 *
 * @param {ReturnType<typeof classifyCellObjects>} groups
 * @returns {string}
 */
export function describeCellObjects(groups) {
  const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
  const parts = [];
  if (groups.meta.length) parts.push('its meta thread');
  if (groups.transcripts.length) parts.push(plural(groups.transcripts.length, 'run transcript', 'run transcripts'));
  if (groups.artifacts.length) parts.push(plural(groups.artifacts.length, 'artifact', 'artifacts'));
  if (groups.other.length) parts.push(plural(groups.other.length, 'other object', 'other objects'));
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/**
 * Which step of its workflow this cell is on — the one to mark in the workflow preview.
 *
 * Read off the RUN LOG, which is the workflow cursor: the single open entry is the current step,
 * run and session. There is no `current_step` prop and never has been — the preview asked for one
 * for as long as it has existed, always got undefined, and so never marked anything.
 *
 * A cell with no open entry has stopped, and the step it stopped ON is still the answer: a
 * completed cell shows where it finished, a failed one where it failed. That is what makes the
 * preview say something useful after the fact rather than going blank the moment it matters.
 *
 * A draft cell has no log at all, and nothing is marked — correctly: it has not been anywhere.
 *
 * @param {{ props?: { log?: Array } }} cell
 * @returns {string|null}
 */
export function activeStep(cell) {
  const log = ((cell && cell.props) || {}).log;
  if (!Array.isArray(log) || log.length === 0) return null;
  const open = log.find(entry => entry && entry.state === 'open');
  return ((open || log[log.length - 1]) || {}).step || null;
}
